import {useState} from "react";
import {getCogsReport} from "../../api/Api";
import {Button, Card, DatePicker, Row, Col, Select} from "antd";
import {ProductType, getOptions} from "../../api/Constants";
import {getSubTypeOptions} from "../../util/inventoryUtils";
import {CogsPurchaseOrderLineItemTable} from "./COGSPurchaseOrderLineItemTable";

export const CogsReport = ({refresh, onRefresh, onApiFail}) => {
    const [startDate, setStartDate] = useState(undefined);
    const [endDate, setEndDate] = useState(undefined);
    const [productType, setProductType] = useState(undefined);
    const [productSubtype, setProductSubtype] = useState(undefined);


    const [report, setReport] = useState(undefined);
    const [disable, setDisable] = useState(false);
    const [numReports, setNumReports] = useState(0);

    const onStartDateChange = (date, dateString) => {
        setStartDate(dateString);
    }

    const onEndDateChange = (date, dateString) => {
        setEndDate(dateString);
    }

    const onTypeChange = (val) => {
        setProductType(val);
    }

    const onSubtypeChange = (val) => {
        setProductSubtype(val);
    }

    const productTypeOptions = getOptions(ProductType) ();
    const productSubtypeOptions = getSubTypeOptions(productType);

    const runReport = () => {
        setDisable(true);

        getCogsReport(startDate, endDate, productType, productSubtype).then(result => {
            setReport(result.map(line => {
                // precompute the total cost for each line
                const {wholesaleCost, supplyCost, testingCost, hours, hourRate} = line.purchaseOrder;
                const fltWholesale = parseFloat(wholesaleCost);
                const fltSupply = parseFloat(supplyCost);
                const fltTest = parseFloat(testingCost);
                const fltLabor = parseFloat((parseFloat(hours) * parseFloat(hourRate)).toFixed(2));
                return {
                    ...line,
                    key: line.id,
                    wholesaleCost: fltWholesale,
                    supplyCost: fltSupply,
                    testingCost: fltTest,
                    laborCost: fltLabor,
                    totalCost: parseFloat((fltWholesale + fltSupply + fltTest + fltLabor).toFixed(2)),
                }
            }))
            setNumReports(numReports+1);
        }).catch(e => {
            // catch error
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        }).finally(() => {
            setDisable(false);
        });
    }

    // compute some aggregate metrics, when report is valid
    let unitQuantity = 0;
    let unitRequested = 0;
    let totalLaborCost = 0, totalWholesaleCost = 0, totalSupplyCost = 0;
    let totalTestingCost = 0;

    // compute metrics only for well defined reports
    if (report) {
        for (const line of report) {
            const {purchaseOrder} = line;
            const {quantity, quantityCreated} = purchaseOrder;
            const {wholesaleCost, supplyCost, testingCost, laborCost} = line;
            unitQuantity += quantityCreated;
            unitRequested += quantity;
            totalWholesaleCost += wholesaleCost;
            totalSupplyCost += supplyCost;
            totalLaborCost += laborCost;
            totalTestingCost += testingCost;
        }
    }

    const totalCost = totalLaborCost + totalWholesaleCost + totalSupplyCost + totalTestingCost;

    const rowStyle = {
        margin: '10px'
    }

    const colStyle = {
        marginTop: '5px',
        marginBottom: '5px'
    }

    const cardStyle = {textAlign: 'left', margin: "5px"}

    const reportTitle = <Row>
        <Col span={2}>
            COGS Report
        </Col>
        <Col style={{color: 'red'}}>
            Data includes all "Packaged Products" constructed through Fulfillment
        </Col>
    </Row>

    return <div>
        <Card style={cardStyle} title={"Run COGS Report"}>
            <Row style={rowStyle}>
                <Col span={4} style={colStyle}><b>Start Date: </b></Col>
                <Col><DatePicker onChange={onStartDateChange} /></Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={4} style={colStyle}><b>End Date: </b></Col>
                <Col><DatePicker onChange={onEndDateChange} /></Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={4} style={colStyle}><b>Product Type:</b></Col>
                <Col>
                    <Select options={productTypeOptions} placeholder={"type"} onChange={onTypeChange} value={productType} popupMatchSelectWidth={false}/>
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={4} style={colStyle}><b>Product Subtype:</b></Col>
                <Col>
                    <Select options={productSubtypeOptions} placeholder={"subtype"} onChange={onSubtypeChange} value={productSubtype} popupMatchSelectWidth={false}/>
                </Col>
            </Row>
            <Button onClick={runReport} disabled={disable}>Run</Button>
        </Card>
        {(numReports > 0) && <Card style={cardStyle} title={reportTitle}>
            <Row style={rowStyle}>
                <Col span={4} style={colStyle}><b>
                    Units Requested:
                </b>
                </Col>
                <Col>
                    {unitRequested}
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={4} style={colStyle}><b>
                    Units Produced:
                </b>
                </Col>
                <Col>
                    {unitQuantity}
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={4} style={colStyle}><b>
                    Wholesale Cost:
                </b>
                </Col>
                <Col>
                    {`$ ${totalWholesaleCost.toFixed(2)}`}
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={4} style={colStyle}><b>
                    Supply Cost:
                </b>
                </Col>
                <Col>
                    {`$ ${totalSupplyCost.toFixed(2)}`}
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={4} style={colStyle}><b>
                    Labor Cost:
                </b>
                </Col>
                <Col>
                    {`$ ${totalLaborCost.toFixed(2)}`}
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={4} style={colStyle}><b>
                    Testing Cost:
                </b>
                </Col>
                <Col style={colStyle}>
                    {`$ ${totalTestingCost.toFixed(2)}`}
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={4} style={colStyle}><b>
                    Total Cost:
                </b>
                </Col>
                <Col>
                    {`$ ${totalCost.toFixed(2)}`}
                </Col>
            </Row>
        </Card>}
        {(numReports > 0) && <Card style={cardStyle} title={`Purchase Orders`}>
            <CogsPurchaseOrderLineItemTable products={report} />
        </Card>}
    </div>
}